import { useTranslation } from 'react-i18next';
import {
  ElementsButton,
  SideOverHeading,
  SideOverHeaderNew,
  SideOverHeaderNewProps,
  SideOverFooter,
  useSideOverContext,
} from '../../UI/components';
import { useNoteReceivableForm } from '../hooks';
import { useCreateNoteReceivableService } from '../service';
import { getCreateNoteReceivableServiceInputFromFormValues } from '../util';
import { NoteReceivableFormSubmitData } from '../types';
import { StandardNoteReceivableForm, StandardNoteReceivableFormVisibilityOptions } from './StandardNoteReceivableForm';
import { MDTailwindMinPixelBreakpointWidth } from '../../util/theme.util';
import { useWindowSize } from 'react-use';

export interface CreateNoteReceivableImplementationPageProps extends Pick<SideOverHeaderNewProps, 'exit'> {
  isLoading?: boolean;
  sideOverHeaderChildren?: React.ReactNode;
  firmID: string;
  clientID: string;
  householdID: string;
  visibilityOptions?: StandardNoteReceivableFormVisibilityOptions;
}

export function CreateNoteReceivableImplementationPage({
  exit,
  firmID,
  clientID,
  isLoading,
  householdID,
  visibilityOptions,
  sideOverHeaderChildren,
}: CreateNoteReceivableImplementationPageProps) {
  const { width: windowWidth } = useWindowSize();
  const { t: tCommon } = useTranslation('common');
  const { closeSideOver, exitRoute } = useSideOverContext();
  const { createNoteReceivableService } = useCreateNoteReceivableService();

  const useNoteReceivableFormReturn = useNoteReceivableForm({
    householdID,
    defaultValueOverrides: {
      ownershipAllocation: 100,
    },
    handleSubmit: (data: NoteReceivableFormSubmitData) => {
      createNoteReceivableService.mutate(
        getCreateNoteReceivableServiceInputFromFormValues({ ...data.formValues, householdID }),
        {
          onSuccess: () => {
            closeSideOver(exitRoute);
          },
        }
      );
    },
  });

  return (
    <>
      <SideOverHeaderNew exit={exit} withShadow className="md:!px-4" exitText={tCommon('cancel')}>
        {sideOverHeaderChildren}
      </SideOverHeaderNew>
      <div className="md:px-10 px-4">
        <SideOverHeading heading={tCommon('note-receivable')} />
        <StandardNoteReceivableForm
          firmID={firmID}
          clientID={clientID}
          className="mb-28 md:mb-36"
          visibilityOptions={visibilityOptions}
          useNoteReceivableFormReturn={useNoteReceivableFormReturn}
        >
          <SideOverFooter>
            <ElementsButton
              icon="check"
              iconPosition="left"
              isLoading={isLoading}
              label={tCommon('save')}
              size={windowWidth < MDTailwindMinPixelBreakpointWidth ? 'small' : 'large'}
            />
          </SideOverFooter>
        </StandardNoteReceivableForm>
      </div>
    </>
  );
}
